import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
  Divider,
  CircularProgress,
} from '@mui/material';
import { adminApi, ApiKeyCreationResponse } from '../services/api';

interface ScopeOption {
  value: string;
  label: string;
  description: string;
  privileged?: boolean;
}

// Mirrored from backend ApiKeyScope enum (src/types/api-key-types.ts).
// Privileged scopes are gated server-side on Admins group membership; we still
// surface them here for admin minting but flag them visually.
const SCOPE_OPTIONS: ScopeOption[] = [
  {
    value: 'legal-research:read',
    label: 'Legal Research',
    description: 'Read-only access to legal research / textbook search.',
  },
  {
    value: 'jobs:read',
    label: 'Job Status',
    description: 'Read job status and results for jobs you created.',
  },
  {
    value: 'ocr:read',
    label: 'OCR',
    description: 'Extract text from PDFs and images via the OCR API.',
  },
  {
    value: 'extract:read',
    label: 'Structured Extraction',
    description: 'Extract structured data from documents into a caller-supplied JSON schema via the Extract API.',
  },
  {
    value: 'analyze:claim',
    label: 'Claim Analysis',
    description: 'Run claim analysis pipelines.',
  },
  {
    value: 'analyze:roa',
    label: 'ROA Analysis',
    description: 'Run record-of-advice analysis.',
  },
  {
    value: 'genesis:commercial',
    label: 'Genesis Commercial',
    description: 'Generate Genesis commercial outputs.',
  },
  {
    value: 'users:create',
    label: 'Create Users (Integrator)',
    description:
      'Mint integrator keys that register / update users in your organisation. ADMIN ONLY.',
    privileged: true,
  },
  {
    value: 'notifications:publish',
    label: 'Notifications Publish',
    description:
      'Used by the prod-deploy CI job to publish AI-drafted notifications into the admin review queue. ADMIN ONLY.',
    privileged: true,
  },
  {
    value: 'admin:read',
    label: 'Admin Read',
    description: 'Read-only admin endpoints. ADMIN ONLY. (Reserved — not yet wired.)',
    privileged: true,
  },
  {
    value: 'admin:write',
    label: 'Admin Write',
    description: 'Mutating admin endpoints. ADMIN ONLY. (Reserved — not yet wired.)',
    privileged: true,
  },
];

interface Props {
  open: boolean;
  orgId: string;
  onClose: () => void;
  onCreated: (created: ApiKeyCreationResponse) => void;
}

const CreateApiKeyDialog: React.FC<Props> = ({ open, orgId, onClose, onCreated }) => {
  const [keyName, setKeyName] = useState('');
  const [scopes, setScopes] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setKeyName('');
    setScopes([]);
    setError(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const toggleScope = (scope: string) => {
    setScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
    );
  };

  const handleSubmit = async () => {
    setError(null);
    if (!keyName.trim()) {
      setError('Key name is required.');
      return;
    }
    if (scopes.length === 0) {
      setError('Select at least one scope.');
      return;
    }
    setSubmitting(true);
    try {
      const created = await adminApi.createApiKey(orgId, {
        keyName: keyName.trim(),
        scopes,
        // Backend overrides this from the deployment stack — value sent here is
        // ignored at the source-of-truth level but the field is required by the
        // request validator.
        environment: 'staging',
      });
      reset();
      onCreated(created);
    } catch (err: any) {
      const status = err?.response?.status;
      const apiMessage = err?.response?.data?.message || err?.response?.data?.error;
      if (status === 403) {
        setError(
          apiMessage ||
            'Forbidden — one of the selected scopes requires Admin group membership.',
        );
      } else {
        setError(apiMessage || err?.message || 'Failed to create API key.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const hasPrivileged = scopes.some(
    (s) => s === 'users:create' || s === 'admin:read' || s === 'admin:write',
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create API Key</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Key name"
            placeholder="e.g. Acme Insurance — landing page integrator"
            value={keyName}
            onChange={(e) => setKeyName(e.target.value)}
            fullWidth
            autoFocus
            inputProps={{ maxLength: 100 }}
            helperText="A descriptive name so you can identify this key later. Up to 100 characters."
          />

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Scopes
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
              Pick the smallest set of scopes the key needs. Scopes flagged
              ADMIN ONLY require you to be in the <code>Admins</code> Cognito
              group — the backend will reject minting otherwise.
            </Typography>
            <FormGroup>
              {SCOPE_OPTIONS.map((opt) => (
                <FormControlLabel
                  key={opt.value}
                  control={
                    <Checkbox
                      checked={scopes.includes(opt.value)}
                      onChange={() => toggleScope(opt.value)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {opt.label}{' '}
                        {opt.privileged && (
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{
                              color: 'warning.main',
                              fontWeight: 600,
                              ml: 0.5,
                            }}
                          >
                            (ADMIN ONLY)
                          </Typography>
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {opt.description}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', mb: 1, mr: 0 }}
                />
              ))}
            </FormGroup>
          </Box>

          {hasPrivileged && (
            <Alert severity="info">
              You've selected an admin-only scope. Treat the resulting key like
              a database password — it can mint or modify users in your
              organisation.
            </Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={18} /> : undefined}
        >
          {submitting ? 'Creating…' : 'Create Key'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateApiKeyDialog;
